import { Aide } from '@/components/ui/aide';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usersAPI, organizationsAPI, whatsappBotAPI, accessProfilesAPI } from '../lib/api';
import { organizationOption } from '../lib/selectOptions';
import { fetchAllPages } from '../lib/fetchAllPages';
import { useAuth } from '../context/AuthContext';
import {
  Building2,
  Check,
  Clock,
  Copy,
  Edit,
  FileText,
  Filter,
  Key,
  Pause,
  Play,
  Plus,
  Search,
  Shield,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { PageHeader } from '../components/ui/page-header';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import { SearchableSelect } from '../components/ui/searchable-select';
import { SUB_ACCOUNT_ROLES, roleLabel } from '../lib/roles';
import { moduleLabel } from '../lib/modules';
import { messageErreur } from '../lib/erreurs';
import { formatDateHeure } from '../lib/formats';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';
import { ProfileDialog } from '../components/users/ProfileDialog';
import { ProfilesPopover } from '../components/users/ProfilesPopover';

// Rôles proposés directement pour un compte : tout le reste (logistique,
// station, maintenance…) passe désormais par un profil d'accès. Les anciens
// rôles restent lisibles et modifiables sur les comptes qui les portent.
const ROLES_DIRECTS = ['ADMIN_SOCIETE', 'CHAUFFEUR', 'CLIENT_PORTAL'];

const VALEUR_PROFIL = 'profile:';
// Entrée d'action du champ « Accès »: ouvre la création d'un profil.
const VALEUR_CREER_PROFIL = '__creer_profil__';

// Ce que permet chaque accès prédéfini, en une ligne.
const RESUME_ROLES = {
  ADMIN_SOCIETE: 'Tous les droits de la société',
  CHAUFFEUR: 'Ses trajets et ses documents',
  CLIENT_PORTAL: 'Son portail client',
  SUPER_ADMIN: 'Gestion de la plateforme',
};

const UsersPage = () => {
  const navigate = useNavigate();
  // Le filtre de profil est dans l'adresse (/users?profil=…): lien partageable
  // et retour arrière. /profiles et ?profils=1 ouvrent la liste des profils.
  const [searchParams, setSearchParams] = useSearchParams();
  const profileFilter = searchParams.get('profil') || 'all';
  const { canManageUsers, isSuperAdmin, isAdminSociete } = useAuth();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');
  const [profilsOuverts, setProfilsOuverts] = useState(false);
  // Société dont le super admin consulte les profils (liste « Profils »).
  const [profilsOrg, setProfilsOrg] = useState('');
  const [profilesLoading, setProfilesLoading] = useState(!isSuperAdmin);
  // Fenêtre de création/modification d'un profil, partagée par la liste des
  // profils et par le formulaire d'un compte (`pourCompte`: le nouveau profil
  // y est choisi aussitôt).
  const [profileDialog, setProfileDialog] = useState({
    open: false,
    editing: null,
    organizationId: '',
    pourCompte: false,
  });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [accessCodeToShow, setAccessCodeToShow] = useState(null); // {code, name}
  const [copied, setCopied] = useState(false);
  // Accès au bot WhatsApp, réservé à l'ADMIN_SOCIETE: `null` tant que la liste
  // n'est pas lue (ou si la lecture échoue), et alors rien du bot n'est affiché.
  const [botAccess, setBotAccess] = useState(null);
  const [botSaving, setBotSaving] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: 'CHAUFFEUR',
    organization_id: '',
    phone: '',
    password: '',
    allowed_modules: null,
    profile_id: null,
  });
  // Profils d'accès connus (ceux de la société de l'administrateur ; pour le
  // super admin, ceux des sociétés consultées), pour le choix du profil et
  // pour lire le nom du profil dans la liste.
  const [profiles, setProfiles] = useState([]);

  const loadProfiles = useCallback(
    async (organizationId) => {
      if (isSuperAdmin && !organizationId) return null;
      try {
        setProfilesLoading(true);
        const liste = await accessProfilesAPI.list(isSuperAdmin ? organizationId : undefined);
        const marques = (liste || []).map((profile) => ({
          ...profile,
          organization_id: profile.organization_id || organizationId || null,
        }));
        setProfiles((current) => {
          // Un administrateur reçoit tous ses profils; pour le super admin, la
          // liste lue remplace seulement celle de la société interrogée.
          const gardes = isSuperAdmin
            ? current.filter((profile) => profile.organization_id !== organizationId)
            : [];
          return [...gardes, ...marques];
        });
        return marques;
      } catch (error) {
        // Sans profils, les trois rôles directs restent utilisables.
        return null;
      } finally {
        setProfilesLoading(false);
      }
    },
    [isSuperAdmin],
  );

  const profileById = useMemo(() => new Map(profiles.map((profile) => [profile.id, profile])), [profiles]);
  const selectedProfile = formData.profile_id ? profileById.get(formData.profile_id) || null : null;
  const profileChosen = Boolean(formData.profile_id);
  const dialogProfiles = useMemo(
    () =>
      isSuperAdmin
        ? profiles.filter((profile) => profile.organization_id === formData.organization_id)
        : profiles,
    [profiles, isSuperAdmin, formData.organization_id],
  );

  // Un compte à code (chauffeur, comptoir, profil « connexion par code ») n'a
  // ni e-mail ni mot de passe obligatoires.
  const isSubAccountRole = profileChosen
    ? selectedProfile?.login_method === 'code'
    : SUB_ACCOUNT_ROLES.has(formData.role);

  // Administrateur, Chauffeur, Portail client (+ Super admin pour le super
  // admin) pour un nouveau compte. Le rôle ancien d'un compte existant reste
  // proposé à part, pour ne pas l'en déloger à la modification.
  const rolesPredefinis = useMemo(
    () => [...ROLES_DIRECTS, ...(isSuperAdmin ? ['SUPER_ADMIN'] : [])],
    [isSuperAdmin],
  );
  const roleAncien =
    formData.role && formData.role !== 'PERSONNEL' && !rolesPredefinis.includes(formData.role)
      ? formData.role
      : null;
  // Compte existant resté sur l'ancien système de droits (rôle historique ou
  // liste de modules): ses droits ne changent pas tant qu'on ne choisit pas
  // un profil.
  const droitsAnciens =
    Boolean(editingUser) && !profileChosen && (Boolean(roleAncien) || Array.isArray(formData.allowed_modules));

  const resumeAcces = useMemo(() => {
    if (profileChosen) {
      if (!selectedProfile) return '';
      const modules = selectedProfile.modules || [];
      const noms = (ecriture) =>
        modules
          .filter((module) => Boolean(module.write) === ecriture)
          .map((module) => moduleLabel(module.code))
          .join(', ');
      return [
        noms(false) && `Consulte : ${noms(false)}`,
        noms(true) && `Modifie : ${noms(true)}`,
        selectedProfile.can_see_money ? 'Montants visibles' : 'Montants masqués',
        selectedProfile.login_method === 'code' ? 'Connexion par code' : 'Connexion par e-mail',
      ]
        .filter(Boolean)
        .join(' · ');
    }
    return RESUME_ROLES[formData.role] || '';
  }, [profileChosen, selectedProfile, formData.role]);

  const focaliserAcces = () => document.getElementById('user-role')?.focus();

  const choisirRole = (value) => {
    if (value === VALEUR_CREER_PROFIL) {
      ouvrirCreationProfil({ pourCompte: true, organizationId: formData.organization_id || '' });
    } else if (value.startsWith(VALEUR_PROFIL)) {
      setFormData((previous) => ({
        ...previous,
        role: 'PERSONNEL',
        profile_id: value.slice(VALEUR_PROFIL.length),
        allowed_modules: null,
      }));
    } else {
      setFormData((previous) => ({ ...previous, role: value, profile_id: null }));
    }
  };

  const userStats = useMemo(() => {
    const active = users.filter((user) => user.is_active && !user.suspended_at).length;
    const suspended = users.filter((user) => user.suspended_at || !user.is_active).length;
    const admins = users.filter((user) => ['SUPER_ADMIN', 'ADMIN_SOCIETE'].includes(user.role)).length;
    const drivers = users.filter((user) => user.role === 'CHAUFFEUR').length;
    const withoutOrg = users.filter((user) => user.role !== 'SUPER_ADMIN' && !user.organization_id).length;
    return { total: users.length, active, suspended, admins, drivers, withoutOrg };
  }, [users]);

  // Seuls les utilisateurs éligibles au bot ont une ligne: c'est le serveur qui
  // décide des rôles concernés, l'écran n'en recopie pas la liste.
  const botRows = useMemo(
    () => new Map((botAccess?.users || []).map((row) => [row.user_id, row])),
    [botAccess],
  );

  const organizationOptions = useMemo(
    () =>
      organizations.map(organizationOption),
    [organizations],
  );
  const orgFilterOptions = useMemo(
    () => [{ value: 'all', label: 'Toutes les organisations' }, ...organizationOptions],
    [organizationOptions],
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersData, orgsData, botData] = await Promise.all([
        // L'API borne chaque lecture (`limit`): on enchaîne les pages jusqu'à la
        // dernière, pour qu'aucun compte ne manque à la liste, aux filtres ni au tri.
        fetchAllPages(({ page, page_size: taille }) =>
          usersAPI.getAll({ skip: (page - 1) * taille, limit: taille }),
        ).then((resultat) => resultat.items),
        isSuperAdmin ? organizationsAPI.getEvery() : [],
        // Une panne côté bot ne doit pas vider la liste des utilisateurs.
        isAdminSociete ? whatsappBotAPI.listAccess().catch(() => null) : null,
      ]);
      setUsers(usersData || []);
      setOrganizations(orgsData || []);
      setBotAccess(botData);
      if (isAdminSociete && !botData) {
        toast.error('Accès au bot WhatsApp indisponible');
      }
      // Les nombres de comptes par profil suivent les créations et suppressions.
      if (!isSuperAdmin) loadProfiles();
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de charger les utilisateurs'));
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, isAdminSociete, loadProfiles]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Administrateur : ses profils, une fois. Super admin : ceux des sociétés
  // qui ont du personnel, et celle choisie dans le formulaire.
  useEffect(() => {
    if (isSuperAdmin && profilsOuverts && profilsOrg) loadProfiles(profilsOrg);
  }, [isSuperAdmin, profilsOuverts, profilsOrg, loadProfiles]);

  // Anciennes adresses (/profiles, ?onglet=profils) et liens ?profils=1: la
  // liste des profils s'ouvre, puis l'adresse est nettoyée.
  useEffect(() => {
    if (!searchParams.has('profils') && !searchParams.has('onglet')) return;
    setProfilsOuverts(true);
    const suite = new URLSearchParams(searchParams);
    suite.delete('profils');
    suite.delete('onglet');
    setSearchParams(suite, { replace: true });
  }, [searchParams, setSearchParams]);

  const orgsAvecProfil = useMemo(
    () => [...new Set(users.filter((user) => user.profile_id && user.organization_id).map((user) => user.organization_id))],
    [users],
  );
  useEffect(() => {
    if (isSuperAdmin) orgsAvecProfil.forEach((organizationId) => loadProfiles(organizationId));
  }, [isSuperAdmin, orgsAvecProfil, loadProfiles]);

  useEffect(() => {
    if (isSuperAdmin && isDialogOpen && formData.organization_id) loadProfiles(formData.organization_id);
  }, [isSuperAdmin, isDialogOpen, formData.organization_id, loadProfiles]);

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: '',
      full_name: '',
      role: 'CHAUFFEUR',
      organization_id: '',
      phone: '',
      password: '',
      allowed_modules: null,
      profile_id: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (updateData.profile_id) {
          // Les droits viennent du profil : plus de liste individuelle.
          updateData.role = 'PERSONNEL';
          delete updateData.allowed_modules;
        } else if (editingUser.profile_id) {
          // Le compte quitte son profil pour un rôle direct.
          updateData.profile_id = null;
        } else {
          delete updateData.profile_id;
        }
        if (!updateData.password) delete updateData.password;
        if (!updateData.email) delete updateData.email;
        if (updateData.role === 'SUPER_ADMIN' || !updateData.organization_id) {
          updateData.organization_id = null;
        }
        await usersAPI.update(editingUser.id, updateData);
        toast.success('Utilisateur mis à jour');
        setIsDialogOpen(false);
        resetForm();
      } else {
        // For sub-accounts, password/email are optional
        const payload = { ...formData };
        if (payload.profile_id) {
          payload.role = 'PERSONNEL';
          delete payload.allowed_modules;
        } else {
          delete payload.profile_id;
        }
        if (isSubAccountRole) {
          delete payload.password;
          if (!payload.email) delete payload.email;
        }
        const created = await usersAPI.create(payload);
        toast.success('Utilisateur créé');
        setIsDialogOpen(false);
        // If the backend returned an access code, show it ONCE
        if (created?.access_code_once) {
          setAccessCodeToShow({
            code: created.access_code_once,
            name: created.full_name || payload.full_name,
          });
        }
        resetForm();
      }
      loadData();
    } catch (error) {
      toast.error(messageErreur(error, "Impossible d'enregistrer l'utilisateur"));
    }
  };

  const handleRegenerateCode = async (user) => {
    if (
      !window.confirm(
        `Régénérer le code d'accès pour ${user.full_name} ? L'ancien code sera invalidé.`,
      )
    )
      return;
    try {
      const res = await usersAPI.regenerateAccessCode(user.id);
      setAccessCodeToShow({
        code: res.access_code,
        name: user.full_name,
      });
      toast.success("Nouveau code d'accès généré");
      loadData();
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de générer un nouveau code'));
    }
  };

  const handleSuspend = async (user) => {
    const reason = window.prompt('Raison de la suspension (optionnel) :', '');
    if (reason === null) return;
    try {
      await usersAPI.suspend(user.id, reason);
      toast.success('Utilisateur suspendu');
      loadData();
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de suspendre cet utilisateur'));
    }
  };

  const handleReactivate = async (user) => {
    try {
      await usersAPI.reactivate(user.id);
      toast.success('Utilisateur réactivé');
      loadData();
    } catch (e) {
      toast.error(messageErreur(e, 'Impossible de réactiver cet utilisateur'));
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      organization_id: user.organization_id,
      phone: user.phone || '',
      password: '',
      allowed_modules: user.allowed_modules ?? null,
      profile_id: user.profile_id || null,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    
    try {
      await usersAPI.delete(id);
      toast.success('Utilisateur supprimé');
      loadData();
    } catch (error) {
      toast.error(messageErreur(error, 'Impossible de supprimer cet utilisateur'));
    }
  };

  // Remplace la ligne d'un utilisateur dans la liste du bot et ajuste le
  // compteur de numéros liés de la différence, sans relire toute la liste.
  const patchBotRow = (userId, nextRow) => {
    setBotAccess((current) => {
      if (!current) return current;
      const previousRow = (current.users || []).find((row) => row.user_id === userId);
      if (!previousRow) return current;
      const merged = { ...previousRow, ...nextRow };
      const delta = (merged.linked ? 1 : 0) - (previousRow.linked ? 1 : 0);
      return {
        ...current,
        linked_count: Math.max(0, (current.linked_count || 0) + delta),
        users: current.users.map((row) => (row.user_id === userId ? merged : row)),
      };
    });
  };

  // Mise à jour optimiste: l'interrupteur bascule tout de suite, et revient à
  // sa position d'avant si le serveur refuse (organisation de démo, rôle non
  // éligible, réseau).
  const handleBotAccessToggle = async (user, enabled) => {
    const previousRow = botRows.get(user.id);
    if (!previousRow) return;
    if (
      !enabled &&
      (previousRow.linked || previousRow.pending) &&
      !window.confirm(
        `Retirer l'accès au bot WhatsApp à ${user.full_name} ? Son numéro lié sera supprimé.`,
      )
    ) {
      return;
    }
    setBotSaving((current) => ({ ...current, [user.id]: true }));
    patchBotRow(
      user.id,
      enabled
        ? { enabled: true }
        : { enabled: false, linked: false, pending: false, phone_masked: null },
    );
    try {
      const savedRow = await whatsappBotAPI.setAccess(user.id, enabled);
      if (savedRow && savedRow.user_id === user.id) patchBotRow(user.id, savedRow);
      toast.success(enabled ? 'Accès au bot accordé' : 'Accès au bot retiré');
    } catch (error) {
      patchBotRow(user.id, previousRow);
      toast.error(messageErreur(error, "Impossible de modifier l'accès au bot"));
    } finally {
      setBotSaving((current) => {
        const { [user.id]: _done, ...rest } = current;
        return rest;
      });
    }
  };

  const filteredUsers = users.filter((user) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      (user.full_name || '').toLowerCase().includes(q) ||
      (user.email || '').toLowerCase().includes(q) ||
      (user.phone || '').toLowerCase().includes(q);
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && user.is_active && !user.suspended_at) ||
      (statusFilter === 'suspended' && user.suspended_at) ||
      (statusFilter === 'inactive' && !user.is_active);
    
    const matchesOrg = orgFilter === 'all' || user.organization_id === orgFilter;
    const matchesProfile = profileFilter === 'all' || user.profile_id === profileFilter;

    return matchesSearch && matchesRole && matchesStatus && matchesOrg && matchesProfile;
  });
  const pagination = usePagination(filteredUsers, {
    reinitialiser: `${searchTerm}|${roleFilter}|${statusFilter}|${orgFilter}|${profileFilter}`,
  });

  // Un tableau vide après une frappe n'est pas une société sans équipe: la
  // phrase et le bouton ne doivent pas être les mêmes.
  const hasUserFilters =
    Boolean(searchTerm) ||
    roleFilter !== 'all' ||
    statusFilter !== 'all' ||
    orgFilter !== 'all' ||
    profileFilter !== 'all';

  const getRoleLabel = roleLabel;

  // Le profil d'un compte du personnel, et le moyen de s'y connecter.
  const usesAccessCode = (user) =>
    user.role === 'PERSONNEL'
      ? profileById.get(user.profile_id)?.login_method === 'code'
      : SUB_ACCOUNT_ROLES.has(user.role);

  // Colour marked the role, not its authority: LOGISTIQUE and CHAUFFEUR shared
  // one hue while FINANCE_STATION was painted destructive red for no reason a
  // reader could act on. Only the two roles that can change other people's
  // access carry a tint now; the rest are neutral labels.
  const getRoleColor = (role) => {
    if (role === 'SUPER_ADMIN') return 'bg-process/15 text-process';
    if (role === 'ADMIN_SOCIETE') return 'bg-info/15 text-info';
    return 'bg-muted text-muted-foreground';
  };

  const ouvrirCreationCompte = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const filtrerParProfil = (id) => {
    const suite = new URLSearchParams(searchParams);
    if (!id || id === 'all') suite.delete('profil');
    else suite.set('profil', id);
    setSearchParams(suite);
  };

  const ouvrirCreationProfil = ({ pourCompte = false, organizationId = '' } = {}) => {
    setProfilsOuverts(false);
    setProfileDialog({ open: true, editing: null, organizationId, pourCompte });
  };

  const ouvrirProfil = (profil) => {
    setProfilsOuverts(false);
    setProfileDialog({
      open: true,
      editing: profil,
      organizationId: profil.organization_id || '',
      pourCompte: false,
    });
  };

  // Après l'enregistrement d'un profil: la liste est relue, et un profil créé
  // depuis le formulaire d'un compte y est choisi aussitôt.
  const apresProfil = async (profil, organizationId, modification) => {
    await loadProfiles(organizationId || undefined);
    if (!modification && profileDialog.pourCompte && profil?.id) {
      setFormData((previous) => ({
        ...previous,
        role: 'PERSONNEL',
        profile_id: profil.id,
        allowed_modules: null,
        organization_id: previous.organization_id || organizationId || '',
      }));
    }
  };

  const profilsAffiches = isSuperAdmin
    ? profiles.filter((profile) => profile.organization_id === profilsOrg)
    : profiles;
  // Un seul filtre « Accès »: les accès prédéfinis, puis les profils.
  const accesFilter = profileFilter !== 'all' ? `${VALEUR_PROFIL}${profileFilter}` : roleFilter;
  const accesFilterOptions = [
    { value: 'all', label: 'Tous les accès' },
    ...[...ROLES_DIRECTS, ...(isSuperAdmin ? ['SUPER_ADMIN'] : [])].map((value) => ({
      value,
      label: getRoleLabel(value),
      description: 'Accès prédéfini',
    })),
    ...profiles.map((profile) => ({
      value: `${VALEUR_PROFIL}${profile.id}`,
      label: profile.name,
      description: 'Profil',
    })),
  ];
  const filtrerParAcces = (valeur) => {
    if (valeur.startsWith(VALEUR_PROFIL)) {
      setRoleFilter('all');
      filtrerParProfil(valeur.slice(VALEUR_PROFIL.length));
    } else {
      setRoleFilter(valeur);
      filtrerParProfil('all');
    }
  };

  if (!canManageUsers) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Shield className="w-16 h-16 mx-auto text-muted-foreground/70 mb-4" />
          <h2 className="text-muted-foreground mb-2">
            Accès refusé
          </h2>
          <p className="text-muted-foreground">
            Vous n'avez pas les permissions pour accéder à cette page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Administration"
        title="Utilisateurs"
        description="Comptes et permissions."
        stats={[
          { label: 'Actifs', valeur: userStats.active, ton: 'positif' },
          { label: 'Suspendus', valeur: userStats.suspended, ton: userStats.suspended ? 'attention' : 'neutre' },
          { label: 'Administrateurs', valeur: userStats.admins },
          {
            label: 'Sans organisation',
            valeur: userStats.withoutOrg,
            ton: userStats.withoutOrg ? 'critique' : 'neutre',
          },
        ]}
        statsPlus={
          <p className="text-sm">
            <span className="text-muted-foreground">Codes terrain : </span>
            <span className="font-medium tabular-nums">{userStats.drivers}</span>
          </p>
        }
        alertesSousTitre
        alertes={
          botAccess
            ? [
                {
                  niveau: 'info',
                  court: `Bot WhatsApp ${botAccess.bot_enabled ? 'activé' : 'désactivé'} · ${botAccess.linked_count || 0} ${(botAccess.linked_count || 0) > 1 ? 'liés' : 'lié'}`,
                  texte: `Bot WhatsApp ${botAccess.bot_enabled ? 'activé' : 'désactivé'} · ${botAccess.linked_count || 0} ${(botAccess.linked_count || 0) > 1 ? 'liés' : 'lié'}.${botAccess.bot_enabled ? '' : ' Les accès accordés prendront effet à l’activation du bot sur la plateforme.'}`,
                },
              ]
            : undefined
        }
        actions={
          <Button onClick={ouvrirCreationCompte}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouveau compte
          </Button>
        }
      />

      {/* The dialog is already state-controlled, so the trigger lives in the
          page header instead of wrapping it. */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Modifier' : 'Créer'} un utilisateur
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-xl border border-border bg-muted/50 p-3">
                <div className="flex items-center gap-1">
                  <p className="text-sm font-bold text-foreground">Résumé d’accès</p>
                  <Aide titre="Résumé d’accès">
                    {isSubAccountRole
                      ? "Ce compte utilisera un code d'accès terrain. L’adresse e-mail et le mot de passe peuvent rester vides."
                      : "Ce compte utilisera une connexion classique avec adresse e-mail et mot de passe."}
                  </Aide>
                </div>
                <div className="mt-2 flex flex-wrap gap-2 text-xs">
                  <span className="rounded-full bg-card px-2 py-1 font-semibold text-muted-foreground">Accès: {profileChosen ? selectedProfile?.name || getRoleLabel('PERSONNEL') : getRoleLabel(formData.role)}</span>
                  <span className="rounded-full bg-card px-2 py-1 font-semibold text-muted-foreground">
                    Organisation: {organizations.find((org) => org.id === formData.organization_id)?.name || 'Non définie'}
                  </span>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Adresse e-mail{' '}
                  {isSubAccountRole && (
                    <span className="text-xs text-muted-foreground/70">(optionnel)</span>
                  )}
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required={!isSubAccountRole}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nom complet</label>
                <Input
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  required
                />
              </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="user-role">Accès</label>
                <select
                  id="user-role"
                  value={profileChosen ? `${VALEUR_PROFIL}${formData.profile_id}` : formData.role}
                  onChange={(e) => choisirRole(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  required
                  disabled={!isSuperAdmin && formData.role === 'SUPER_ADMIN'}
                >
                  {roleAncien ? (
                    <optgroup label="Ancien système">
                      <option value={roleAncien}>{roleLabel(roleAncien)}</option>
                    </optgroup>
                  ) : null}
                  <optgroup label="Accès prédéfinis">
                    {rolesPredefinis.map((value) => (
                      <option key={value} value={value}>
                        {roleLabel(value)}
                      </option>
                    ))}
                  </optgroup>
                  {dialogProfiles.length > 0 || (profileChosen && !selectedProfile) ? (
                    <optgroup label="Vos profils">
                      {profileChosen && !selectedProfile ? (
                        <option value={`${VALEUR_PROFIL}${formData.profile_id}`}>Personnel</option>
                      ) : null}
                      {dialogProfiles.map((profile) => (
                        <option key={profile.id} value={`${VALEUR_PROFIL}${profile.id}`}>
                          {`${profile.name} (${profile.users_count || 0})`}
                        </option>
                      ))}
                    </optgroup>
                  ) : null}
                  <option value={VALEUR_CREER_PROFIL}>+ Créer un profil</option>
                </select>
                {droitsAnciens ? (
                  <p className="mt-1 flex flex-wrap items-center gap-2 text-xs" data-testid="droits-anciens">
                    <span className="font-medium text-flame-ink">Droits personnalisés (ancien système)</span>
                    <button
                      type="button"
                      className="font-medium text-info hover:underline"
                      onClick={focaliserAcces}
                    >
                      Passer à un profil
                    </button>
                  </p>
                ) : resumeAcces ? (
                  <p className="mt-1 text-xs text-muted-foreground" data-testid="acces-resume">
                    {resumeAcces}
                  </p>
                ) : null}
                {isSubAccountRole && (
                  <p className="text-xs text-info mt-1">
                    Un code d'accès à 6 chiffres sera généré automatiquement.
                  </p>
                )}
              </div>
              {organizations.length > 0 && formData.role !== 'SUPER_ADMIN' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Organisation</label>
                  <SearchableSelect
                    value={formData.organization_id || ''}
                    onValueChange={(organizationId) =>
                      setFormData({
                        ...formData,
                        organization_id: organizationId,
                        // Un profil appartient à une société : on le quitte.
                        ...(formData.profile_id ? { profile_id: null, role: 'CHAUFFEUR' } : {}),
                      })
                    }
                    aria-label="Organisation"
                    placeholder="Sélectionner une organisation"
                    searchPlaceholder="Rechercher une organisation…"
                    emptyMessage="Aucune organisation trouvée"
                    options={organizationOptions}
                    className="h-10"
                    required={formData.role !== 'SUPER_ADMIN'}
                  />
                </div>
              )}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <Input
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                />
              </div>
              {!isSubAccountRole && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Mot de passe{' '}
                    {editingUser ? '(laisser vide pour ne pas changer)' : ''}
                  </label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    required={!editingUser}
                  />
                </div>
              )}
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Annuler
                </Button>
                <Button type="submit">
                  {editingUser ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

      <div className="bg-card rounded-xl shadow-sm border border-border">
        <div className="sticky top-0 z-20 rounded-t-xl bg-card/95 backdrop-blur p-4 border-b border-border">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground/70" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="max-w-xs"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground/70" />
              <SearchableSelect
                value={accesFilter}
                onValueChange={filtrerParAcces}
                aria-label="Filtrer par accès"
                placeholder="Tous les accès"
                searchPlaceholder="Rechercher un accès…"
                emptyMessage="Aucun accès trouvé"
                options={accesFilterOptions}
                className="h-10 w-[200px]"
              />
            </div>
            <div className="flex items-center gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">Tous les statuts</option>
                <option value="active">Actifs</option>
                <option value="suspended">Suspendus</option>
                <option value="inactive">Inactifs</option>
              </select>
            </div>
            {isSuperAdmin && (
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground/70" />
                <SearchableSelect
                  value={orgFilter}
                  onValueChange={setOrgFilter}
                  aria-label="Filtrer par organisation"
                  placeholder="Toutes les organisations"
                  searchPlaceholder="Rechercher une organisation…"
                  emptyMessage="Aucune organisation trouvée"
                  options={orgFilterOptions}
                  className="h-10 w-[220px]"
                />
              </div>
            )}
            <div className="ml-auto flex items-center gap-2">
              <ProfilesPopover
                open={profilsOuverts}
                onOpenChange={setProfilsOuverts}
                count={isSuperAdmin && !profilsOrg ? profiles.length : profilsAffiches.length}
                profiles={profilsAffiches}
                loading={profilesLoading && profilsAffiches.length === 0}
                organizations={organizations}
                organizationId={profilsOrg}
                onOrganizationChange={setProfilsOrg}
                onEdit={ouvrirProfil}
                onDeleted={(profil) =>
                  loadProfiles(isSuperAdmin ? profil.organization_id || profilsOrg : undefined)
                }
                onSeeAccounts={(profil) => {
                  setProfilsOuverts(false);
                  filtrerParProfil(profil.id);
                }}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => ouvrirCreationProfil({ organizationId: isSuperAdmin ? profilsOrg : '' })}
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Nouveau profil
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <LoadingState shape="table" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Adresse e-mail</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Organisation</TableHead>
                <TableHead>Dernière connexion</TableHead>
                <TableHead>Statut</TableHead>
                {botAccess ? <TableHead>Accès bot</TableHead> : null}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagination.lignes.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.full_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.role === 'PERSONNEL' && profileById.get(user.profile_id) ? (
                      <button
                        type="button"
                        data-testid={`compte-profil-${user.id}`}
                        title="Ouvrir ce profil d’accès"
                        onClick={() => ouvrirProfil(profileById.get(user.profile_id))}
                        className={`px-2 py-1 rounded-full text-xs font-medium hover:underline ${getRoleColor(
                          user.role
                        )}`}
                      >
                        {profileById.get(user.profile_id).name}
                      </button>
                    ) : (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(
                          user.role
                        )}`}
                      >
                        {getRoleLabel(user.role)}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.organization_id && organizations.find((o) => o.id === user.organization_id) ? (
                      <button
                        onClick={() => navigate(`/organizations/${user.organization_id}`)}
                        className="flex items-center gap-1 text-info hover:text-info hover:underline"
                      >
                        <Building2 className="w-3 h-3" />
                        {organizations.find((o) => o.id === user.organization_id)?.name}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">Aucune</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      {user.last_login ? formatDateHeure(user.last_login) : 'Jamais'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.suspended_at
                          ? 'bg-flame/15 text-flame-ink'
                          : user.is_active
                            ? 'bg-success/15 text-success'
                            : 'bg-destructive/15 text-destructive'
                      }`}
                    >
                      {user.suspended_at ? 'Suspendu' : user.is_active ? 'Actif' : 'Inactif'}
                    </span>
                  </TableCell>
                  {botAccess ? (
                    <TableCell>
                      {(() => {
                        const botRow = botRows.get(user.id);
                        if (!botRow) {
                          return <span className="text-muted-foreground/70">—</span>;
                        }
                        return (
                          <div className="flex flex-wrap items-center gap-2">
                            <Switch
                              checked={Boolean(botRow.enabled)}
                              disabled={Boolean(botSaving[user.id])}
                              onCheckedChange={(checked) => handleBotAccessToggle(user, checked)}
                              aria-label={`Accès au bot WhatsApp pour ${user.full_name}`}
                            />
                            <Badge variant={botRow.linked ? 'success' : botRow.pending ? 'warning' : 'muted'}>
                              {botRow.linked ? 'Lié' : botRow.pending ? 'En attente' : 'Non lié'}
                            </Badge>
                            {botRow.linked && botRow.phone_masked ? (
                              <span className="text-xs tabular-nums text-muted-foreground">
                                {botRow.phone_masked}
                              </span>
                            ) : null}
                          </div>
                        );
                      })()}
                    </TableCell>
                  ) : null}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {isSuperAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Voir le journal des actions"
                          onClick={() => navigate(`/audit?user_id=${user.id}`)}
                        >
                          <FileText className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      )}
                      {usesAccessCode(user) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Régénérer le code d'accès"
                          onClick={() => handleRegenerateCode(user)}
                        >
                          <Key className="w-4 h-4 text-info" />
                        </Button>
                      )}
                      {user.suspended_at ? (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Réactiver"
                          onClick={() => handleReactivate(user)}
                        >
                          <Play className="w-4 h-4 text-success" />
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Suspendre"
                          onClick={() => handleSuspend(user)}
                          disabled={user.role === 'SUPER_ADMIN'}
                        >
                          <Pause className="w-4 h-4 text-flame-ink" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Modifier"
                        aria-label="Modifier"
                        onClick={() => handleEdit(user)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Supprimer"
                        aria-label="Supprimer"
                        onClick={() => handleDelete(user.id)}
                        disabled={user.role === 'SUPER_ADMIN'}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {!loading && filteredUsers.length > 0 ? (
          <BarrePagination pagination={pagination} nom="utilisateurs" />
        ) : null}

        {/* Une équipe filtrée à vide et une société où personne n'a encore
            de compte n'appellent pas le même geste. */}
        {!loading && filteredUsers.length === 0 && (
          <EmptyState
            variant={hasUserFilters ? 'filtered' : 'empty'}
            icon={UserPlus}
            title={
              hasUserFilters
                ? 'Aucun utilisateur ne correspond'
                : 'Vous êtes seul sur ce compte'
            }
            description={
              hasUserFilters
                ? 'Vos utilisateurs sont toujours là : la recherche ou les filtres de rôle, de statut ou d’organisation en masquent l’affichage.'
                : 'Chaque personne de votre équipe reçoit son propre accès et ne voit que ce que son rôle autorise : un dispatcher n’ouvre pas la comptabilité, un chauffeur ne voit que ses trajets. Vous saurez aussi qui a fait quoi.'
            }
            actionLabel={
              hasUserFilters ? undefined : 'Inviter quelqu’un de l’équipe'
            }
            onAction={ouvrirCreationCompte}
            className="border-0"
          />
        )}
      </div>

      <ProfileDialog
        open={profileDialog.open}
        onOpenChange={(open) => setProfileDialog((previous) => ({ ...previous, open }))}
        editing={profileDialog.editing}
        organizationId={profileDialog.organizationId}
        organizations={organizations}
        onSaved={apresProfil}
      />

      {/* Access Code display modal (shown ONCE after create or regenerate) */}
      {accessCodeToShow && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <Key className="w-6 h-6 text-info" />
              <h3>Code d'accès à 6 chiffres</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Communiquez ce code à{' '}
              <strong>{accessCodeToShow.name}</strong> de manière sécurisée. Il
              ne sera plus visible après fermeture de cette fenêtre.
            </p>
            <div className="bg-info/10 border-2 border-info/30 rounded-lg p-6 text-center mb-4">
              <div className="text-4xl font-mono font-bold tracking-[0.4em] text-info">
                {accessCodeToShow.code}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => copyCode(accessCodeToShow.code)}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 mr-1" /> Copié
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" /> Copier
                  </>
                )}
              </Button>
              <Button
                className="flex-1"
                onClick={() => setAccessCodeToShow(null)}
              >
                J'ai noté le code
              </Button>
            </div>
            <p className="text-xs text-muted-foreground/70 mt-3 text-center">
              Pour des raisons de sécurité, ce code ne sera plus jamais affiché.
              En cas de perte, régénérez-le.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};


export default UsersPage;
